import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Trash2, UploadCloud } from "lucide-react";
import { AuthContext } from "../../../context/authContext";
import {
  updatePayoutDetails,
  uploadPayoutReceipt,
} from "../../../reducers/payouts.slice";
import {
  updateWithdrawalRequest,
  uploadWithdrawalReceipt,
} from "../../../reducers/giftcard.slice";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const UpdatePayoutFormPage = ({ dialogMode = false, paymentDetails: paymentDetailsProp, onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const paymentDetails = paymentDetailsProp || location?.state?.paymentDetails;

  const [showLoader, setShowLoader] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [openConfirmPayment, setOpenConfirmPayment] = useState(false);
  const [formData, setFormData] = useState({
    amount: paymentDetails?.amount ?? "",
    payment_ref_id: paymentDetails?.payment_ref_id ?? "",
    payment_date: new Date().toISOString().split("T")[0],
    notes: paymentDetails?.notes ?? "",
  });

  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (showLoader) {
        event.preventDefault();
        event.returnValue = "API is still processing. Please wait.";
      }
    };

    const blockRefresh = (event) => {
      if (
        showLoader &&
        (event.key === "F5" || (event.ctrlKey && event.key === "r"))
      ) {
        event.preventDefault();
        showToast("API is still processing. Please wait.", "warning");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", blockRefresh);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", blockRefresh);
    };
  }, [showLoader]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileUpload(files);
  };

  const handleFileUpload = (files) => {
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFormData = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isDisableButton = !formData?.amount;

  const handleUpdatePayment = () => {
    setShowLoader(true);
    const docFormData = new FormData();
    uploadedFiles.forEach((file) => {
      docFormData.append("files", file);
    });
    docFormData.append("id", paymentDetails?.id);
    docFormData.append("document_type", "RECEIPT");
    const payload = {
      ...formData,
      id: paymentDetails?.id,
      status: "APPROVED",
    };
    if (!formData?.notes) delete payload.notes;

    if (uploadedFiles.length === 0) {
      dispatch(updateWithdrawalRequest(payload)).then((res) => {
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          setShowLoader(false);
          showToast(res?.error?.message, "error");
          return;
        }
        showToast("Successfully Approved Withdrawal Request", "success");
        setShowLoader(false);
        if (dialogMode && onSuccess) onSuccess();
        else navigate("/dashboard/payouts/withdraw-request");
      });
      return;
    }

    dispatch(uploadWithdrawalReceipt({ docFormData })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      showToast("Successfully Uploaded Files", "success");
      if (res?.payload) {
        dispatch(updateWithdrawalRequest(payload)).then((res) => {
          if (res?.error && Object.keys(res?.error)?.length > 0) {
            setShowLoader(false);
            showToast(res?.error?.message, "error");
            return;
          }
          showToast("Successfully Approved Withdrawal Request", "success");
          setShowLoader(false);
          if (dialogMode && onSuccess) onSuccess();
          else navigate("/dashboard/payouts/withdraw-request");
        });
      }
    });
  };

  return (
    <>
      {showLoader && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Loading...
        </div>
      )}

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          padding: dialogMode ? 0 : isMobile ? "8px 0 32px" : "24px 0 48px",
        }}
      >
        <div style={{ width: "100%", maxWidth: 480 }}>
          {!dialogMode && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#c3c8cc",
                background: "transparent",
                border: "none",
                padding: 0,
                marginBottom: 18,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              <ArrowLeft size={16} /> Back
            </button>
          )}

          <h2 style={{ fontSize: "1.4rem", fontWeight: 700, color: "#fff", margin: "0 0 20px" }}>
            Approve Payment
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Payment Date (readonly) */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Label htmlFor="payment_date">Payment Date</Label>
              <Input
                id="payment_date"
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleFormData}
                readOnly
              />
            </div>

            {/* Amount */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                name="amount"
                type="text"
                value={formData.amount}
                placeholder="Enter Amount"
                onChange={handleFormData}
                onKeyDown={(e) => {
                  const invalidChars = ["e", "E", ",", "+", "-"];
                  if (e.key === "." && e.target.value.includes(".")) {
                    e.preventDefault();
                    return;
                  }
                  if (
                    invalidChars.includes(e.key) ||
                    (!/^\d$/.test(e.key) &&
                      e.key !== "." &&
                      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
                      !e.ctrlKey &&
                      !e.metaKey)
                  ) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  const pasteData = e.clipboardData.getData("text");
                  if (!/^\d*\.?\d*$/.test(pasteData)) e.preventDefault();
                }}
                onWheel={(e) => e.target.blur()}
              />
              {formData.amount !== "" && parseFloat(formData.amount) <= 0 && (
                <span style={{ color: "#f27059", fontSize: 12 }}>Amount cannot be zero or negative</span>
              )}
            </div>

            {/* Payment Ref ID */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Label htmlFor="payment_ref_id">Payment Ref ID</Label>
              <Input
                id="payment_ref_id"
                type="text"
                name="payment_ref_id"
                value={formData.payment_ref_id}
                placeholder="Enter Payment Ref ID"
                onChange={handleFormData}
                onKeyDown={(e) => {
                  if (
                    !/^[a-zA-Z0-9]$/.test(e.key) &&
                    !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End", " "].includes(e.key) &&
                    !e.ctrlKey &&
                    !e.metaKey
                  ) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  const pasteData = e.clipboardData.getData("text");
                  if (!/^[a-zA-Z0-9\s]*$/.test(pasteData)) e.preventDefault();
                }}
              />
            </div>

            {/* Notes */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                placeholder="Describe in detail..."
                onChange={handleFormData}
                rows={4}
              />
            </div>

            {/* Upload area */}
            <button
              type="button"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              style={{
                width: "100%",
                minHeight: 160,
                border: `1px dashed ${dragOver ? "#00ADB5" : "rgba(255,255,255,0.28)"}`,
                borderRadius: 8,
                marginTop: 4,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                background: dragOver ? "rgba(0,173,181,0.08)" : "rgba(255,255,255,0.025)",
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <UploadCloud size={36} color="#00ADB5" />
                <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>
                  Click here or Drop a file to upload payment receipt
                </span>
                <span style={{ color: "#8b9296", fontSize: 12 }}>
                  (Accepted File Types: .jpg, .png, .pdf)
                </span>
              </span>
              <input
                id="file-input"
                type="file"
                accept=".jpg,.png,.pdf"
                style={{ display: "none" }}
                onChange={handleFileInputChange}
                multiple
              />
            </button>

            {/* Uploaded files */}
            <div>
              <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, marginBottom: 10 }}>
                Uploaded Files
              </div>
              {uploadedFiles.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        border: "1px solid rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.025)",
                        padding: 12,
                        borderRadius: 8,
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          minWidth: 0,
                          color: "#c3c8cc",
                          fontSize: 13,
                        }}
                      >
                        <FileText size={15} style={{ flexShrink: 0 }} />
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                        style={{ color: "#f27059", flexShrink: 0 }}
                      >
                        <Trash2 size={14} /> Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#8b9296", fontSize: 13 }}>No files uploaded</div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <Button variant="outline" onClick={() => { if (dialogMode && onCancel) onCancel(); else navigate(-1); }}>
                Cancel
              </Button>
              <Button
                onClick={() => setOpenConfirmPayment(true)}
                disabled={isDisableButton}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Payment Dialog */}
      <Dialog open={openConfirmPayment} onOpenChange={setOpenConfirmPayment}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Confirm Payment?</DialogTitle>
            <DialogDescription>
              You are about to confirm this withdraw request. Once confirmed, the member will be
              notified.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenConfirmPayment(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePayment}>Confirm Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpdatePayoutFormPage;
